import React from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";

import { colors } from "../dark-hero/colors";
import { STAT_LABELS, STATS_FOOTNOTE } from "./honest-buildContent";

function isPresent(value) {
  return value != null;
}

function formatCount(value) {
  return Number(value).toLocaleString("en-GB");
}

function rowSxFor(count) {
  const desktopMax =
    count >= 5 ? "52rem" : count >= 4 ? "44rem" : count === 3 ? "34rem" : count === 2 ? "22rem" : "12rem";
  return {
    display: "grid",
    gridTemplateColumns: {
      xs: count === 1 ? "1fr" : count === 3 ? "repeat(3, minmax(0, 1fr))" : "repeat(2, minmax(0, 1fr))",
      md: `repeat(${count}, minmax(0, 1fr))`,
    },
    alignItems: "stretch",
    gap: { xs: 1, md: 1.5 },
    width: "100%",
    maxWidth: { xs: count === 3 ? "24rem" : "22rem", md: desktopMax },
    mx: "auto",
  };
}

const cellSx = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  px: { xs: 0.75, md: 1.25 },
  py: { xs: 1.25, md: 1.5 },
  border: `1px solid ${colors.border.default}`,
  borderRadius: "12px",
  background: "linear-gradient(180deg, rgba(255,255,255,0.03), transparent)",
  minWidth: 0,
  minHeight: { xs: "5.5rem", md: "6.25rem" },
  height: "100%",
  boxSizing: "border-box",
};

const valueSx = {
  m: 0,
  display: "flex",
  alignItems: "flex-end",
  justifyContent: "center",
  height: { xs: "1.4rem", md: "1.7rem" },
  fontFamily: "'Inter', system-ui, sans-serif",
  fontSize: { xs: "1.15rem", md: "1.5rem" },
  fontWeight: 700,
  letterSpacing: "-0.03em",
  fontVariantNumeric: "tabular-nums",
  color: colors.text.primary,
  lineHeight: 1,
};

const labelSx = {
  m: 0,
  mt: 0.5,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: { xs: "2.4em", md: "2.6em" },
  fontSize: { xs: "0.65rem", md: "0.72rem" },
  lineHeight: 1.3,
  color: colors.text.muted,
};

const sublabelSx = {
  m: 0,
  mt: 0.25,
  fontSize: { xs: "0.6rem", md: "0.66rem" },
  lineHeight: 1.25,
  color: colors.text.secondary,
};

const footnoteSx = {
  mt: 1,
  fontSize: "0.7rem",
  color: colors.text.muted,
  textAlign: "center",
};

function formatEligibleSplit(split) {
  if (!isPresent(split?.EU) || !isPresent(split?.NA)) return null;
  return `EU ${formatCount(split.EU)} · NA ${formatCount(split.NA)}`;
}

export default function StatsRow({ stats }) {
  const eligibleSplit = formatEligibleSplit(stats?.community?.eligibleByRegion);
  const items = [
    {
      key: "signups",
      label: STAT_LABELS.signupsTotal,
      value: stats?.community?.signupsTotal,
    },
    {
      key: "rejected",
      label: STAT_LABELS.rejectedTotal,
      value: stats?.community?.rejectedTotal,
    },
    {
      key: "eligible",
      label: STAT_LABELS.eligibleTotal,
      value: stats?.community?.eligibleTotal,
      sublabel: eligibleSplit,
    },
    {
      key: "matches",
      label: STAT_LABELS.matchesTotal,
      value: stats?.community?.matchesTotal,
    },
    {
      key: "discord",
      label: STAT_LABELS.discordMembers,
      value: stats?.discord?.memberCount,
    },
  ].filter((item) => isPresent(item.value));

  if (items.length === 0) return null;

  return (
    <Box>
      <Box sx={rowSxFor(items.length)}>
        {items.map((item) => (
          <Box key={item.key} sx={cellSx}>
            <Typography sx={valueSx}>{formatCount(item.value)}</Typography>
            <Typography sx={labelSx}>{item.label}</Typography>
            {item.sublabel ? <Typography sx={sublabelSx}>{item.sublabel}</Typography> : null}
          </Box>
        ))}
      </Box>
      <Typography sx={footnoteSx}>{STATS_FOOTNOTE}</Typography>
    </Box>
  );
}

StatsRow.propTypes = {
  stats: PropTypes.shape({
    community: PropTypes.shape({
      signupsTotal: PropTypes.number,
      rejectedTotal: PropTypes.number,
      eligibleTotal: PropTypes.number,
      eligibleByRegion: PropTypes.shape({
        EU: PropTypes.number,
        NA: PropTypes.number,
      }),
      matchesTotal: PropTypes.number,
    }),
    discord: PropTypes.shape({
      memberCount: PropTypes.number,
    }),
  }),
};
